import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Continuous "ticker" marquee for the Dive Deeper section.
 * Same pattern as the CloudMarquee but for the 9 deep-dive subpages.
 *
 * Compact, horizontally-scrolling strip — much smaller footprint than
 * the previous 320×200px card grid (which was ~880px tall section).
 * New strip is ~110px tall.
 *
 * v34.87 (Feb 2026) — replaces the previous "dive-marquee-track" with
 * a tighter pill-style strip.
 */
const DiveDeeperMarquee = ({ items = [] }) => {
  // Duplicate so the loop is seamless
  const doubled = [...items, ...items];

  return (
    <div
      className="relative overflow-hidden py-2"
      data-testid="dive-deeper-marquee"
    >
      {/* Edge fade — feathers the marquee into the page background */}
      <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-slate-950 bg-gradient-to-r from-slate-950 to-transparent" />
      <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-slate-950 bg-gradient-to-l from-slate-950 to-transparent" />

      <div
        className="flex gap-3 animate-marquee-left hover:[animation-play-state:paused] motion-reduce:animate-none"
        style={{ width: 'max-content' }}
      >
        {doubled.map((card, idx) => {
          const CardIcon = card.Icon;
          const isOriginal = idx < items.length;
          return (
            <Link
              key={`${card.to}-${idx}`}
              to={card.to}
              data-testid={`dive-deeper-${card.tag.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-${isOriginal ? 'a' : 'b'}`}
              aria-hidden={!isOriginal}
              className={`group flex items-center gap-2.5 shrink-0 rounded-full border border-white/10 bg-slate-900 hover:bg-slate-800 px-3 py-2 transition-all ${card.hover}`}
            >
              {/* Tiny glowy icon */}
              <div className={`w-7 h-7 rounded-lg ${card.badge} flex items-center justify-center shrink-0`}>
                <CardIcon className={`w-3.5 h-3.5 ${card.iconColor}`} />
              </div>

              {/* Tag + title stacked */}
              <div className="flex flex-col leading-tight min-w-0">
                <span className={`text-[9px] font-mono uppercase tracking-[0.16em] ${card.text}`}>
                  {card.tag}
                </span>
                <span className="text-xs font-bold text-white whitespace-nowrap">
                  {card.title}
                </span>
              </div>

              {/* Subtle arrow that nudges on hover */}
              <ArrowRight className={`w-3.5 h-3.5 ${card.text} opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0`} />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default DiveDeeperMarquee;
